// PreviewTester/components/Controls.js
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import styles from "./Controls.module.css";
import { TemplateValidator } from "../PreviewTester/utils/templateValidator";
import ActionFieldsBuilder from "../../AiAgentWorkFlow/ActionFieldsBuilder";
import SkillFieldsMapper from "../PreviewTester/components/SkillFieldsMapper";
import {
  isButtonStep,
  isButtonGroupStep,
  isStyleStep,
  isStructuralStep,
  isMediaStep,
  isCombinedStep,
  cleanSelectors,
  createButtonStep,
  createButtonGroupStep,
  createStyleStep,
  createStructuralStep,
  createMediaStep,
  createCombinedStep,
} from "../PreviewTester/utils/transformationSchema";

import { fetchIntents } from "../PreviewTester/utils/intentsApi";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;
console.log("Can you see me here nowere?");

const Controls = ({
  scrollMode,
  setScrollMode,
  layoutDirection,
  setLayoutDirection,
  dataType,
  isSampleArray,
  arrayLayoutDirection,
  setArrayLayoutDirection,
  hasParsedData,
  template,
  configId = "",
  kbId = "tenant4__appv2",
  statusCode = "201",
  onSaveSuccess,
  onAddAction,
  actions = [],
  onRemoveAction,
  onUpdateAction,
  transformationRules = [],
  caseType = "unknown",
  modalMode = false,
  onTemplateDataReady = null,
  parsedSample = null,
  buttons = [],
  queryId = { queryId },
  templateLines = [],
}) => {
  console.log("queryIddrulid", queryId);
  console.log("[🔘 BUTTON FLOW] Controls rendered with buttons:", buttons);
  console.log("[🔘 BUTTON FLOW] buttons length:", buttons.length);

  const safeTemplate = typeof template === "string" ? template : "";

  const region = process.env.REACT_APP_REGION || "us";

  const meData = useSelector((state) => state.auth.meData);
  console.log("[Controls] 🪪 meData:", meData);

  const tenantId = useMemo(() => {
    const fromAuth = meData?.tenant_info?.id;
    if (fromAuth) return fromAuth;
    if (kbId && typeof kbId === "string") {
      const parts = kbId.split("__");
      return parts[0] || "";
    }
    return "";
  }, [meData, kbId]);

  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [editingActionId, setEditingActionId] = useState(null);
  const [selectedButtonText, setSelectedButtonText] = useState("");
  const [selectedActionType, setSelectedActionType] = useState("api_config");
  const [selectedActionId, setSelectedActionId] = useState("");
  const [availableApiConfigs, setAvailableApiConfigs] = useState([]);
  const [availableChains, setAvailableChains] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const [actionError, setActionError] = useState(null);
  const modalRef = useRef(null);

  // Skill (intent) selector state
  const [intents, setIntents] = useState([]);
  const [loadingIntents, setLoadingIntents] = useState(false);
  const [intentsError, setIntentsError] = useState(null);
  const [selectedIntentType, setSelectedIntentType] = useState("");

  // Skill field mapping state
  const [intentFieldMapping, setIntentFieldMapping] = useState({});
  const [intentMappingValidity, setIntentMappingValidity] = useState(true);

  const availableButtons = buttons;

  console.log("[🔘 BUTTON FLOW] availableButtons:", availableButtons);
  console.log(
    "[🔘 BUTTON FLOW] availableButtons length:",
    availableButtons.length,
  );

  const [selectedApiConfigDetails, setSelectedApiConfigDetails] =
    useState(null);
  const [responseToRequestMapping, setResponseToRequestMapping] = useState({});

  const showDirectionControl = isSampleArray || dataType === "array";

  // PreviewTester/components/Controls.js

  /**
   * Find the innermost for-loop that encloses a given template row.
   *
   * Walks backwards from `rowIndex`, looking for `for_loop_start` lines
   * whose depth is strictly less than the target row's depth. For each
   * candidate, verifies that its matching `endfor` appears AFTER the
   * target row — otherwise the loop is a sibling, not an ancestor.
   *
   * Returns the loop variable name (e.g. "staff", "room", "item") of
   * the first valid enclosing loop, or `null` if the row is not inside
   * any loop.
   *
   * Why this matters: the admin dropdown uses the returned loop var to
   * decide which array's fields to surface as `item.*`. If it
   * picks the wrong loop (e.g. an outer loop instead of the innermost
   * one), the dropdown suggests the wrong field set for the button.
   */
  const findEnclosingLoopVar = (rowIndex, lines) => {
    if (!lines || rowIndex < 0 || rowIndex >= lines.length) return null;

    const targetDepth = lines[rowIndex]?.depth || 0;

    // Walk backwards from the target row. The innermost enclosing loop
    // is the closest `for_loop_start` before the row whose depth is
    // strictly less than the row's own depth.
    for (let i = rowIndex - 1; i >= 0; i--) {
      const line = lines[i];
      if (!line) continue;
      if (line.type !== "for_loop_start") continue;

      const loopDepth = line.depth || 0;
      if (loopDepth >= targetDepth) continue;

      // Verify this loop actually encloses the target row by finding
      // its matching `endfor` and checking it comes after `rowIndex`.
      //
      // Starting from the loop's own `for_loop_start`, walk forward
      // with a balance counter. The matching `endfor` is the one that
      // brings the balance back to zero. Nested inner loops raise the
      // balance and their `endfor`s lower it, so only this loop's own
      // closer zeros it out.
      let balance = 0;
      let endIdx = -1;

      for (let j = i; j < lines.length; j++) {
        const r = lines[j];
        if (!r) continue;

        if (r.type === "for_loop_start") {
          balance++;
        } else if (r.type === "for_loop_end") {
          balance--;
          if (balance === 0) {
            endIdx = j;
            break;
          }
        }
      }

      if (endIdx !== -1 && endIdx > rowIndex) {
        // This loop genuinely wraps the target row.
        return line.loopVariable || null;
      }
      // Otherwise this loop's endfor came before the target row — it
      // is a sibling loop, not an ancestor. Keep walking backwards.
    }

    return null;
  };

  // PreviewTester/components/Controls.js

  /**
   * Extract the fields visible from the button's position in the template.
   *
   * Trigger mappings are SCREEN-SCOPED. Two namespaces can appear:
   *
   *   • item.*
   *       Fields of the row the button lives in — the current iteration
   *       of the innermost enclosing for-loop. Only emitted when the
   *       button is inside a loop.
   *
   *   • response_data.*
   *       Fields of the screen's outer data. Emitted always.
   *
   * In BOTH namespaces, array-valued keys are SKIPPED ENTIRELY — the
   * container name itself (e.g. `staff`, `rooms`) and anything inside
   * it. Arrays are containers for other loops, and their contents are
   * only reachable from within that loop as `item.*`.
   *
   * So a row like:
   *   { id, name, staff: [...], rooms: [...] }
   * yields only:
   *   item.id, item.name
   * — never `item.staff` or `item.staff.*`.
   *
   * The literal loop variable name in the template (room, staff, item,
   * etc.) is intentionally NOT surfaced — the surface is always
   * normalised to `item`, matching what the client runtime
   * substitutes regardless of what the template called its loop var.
   *
   * Nested non-array objects are traversed deeply (e.g.
   * response_data.meta.version) because they're mappable leaves.
   */
  const extractFieldsForContext = (sample, loopVar) => {
    if (!sample) return [];

    // ---- helpers -----------------------------------------------------
    const extractFields = (obj, prefix = "") => {
      if (!obj || typeof obj !== "object") return [];

      const fields = [];
      for (const [key, value] of Object.entries(obj)) {
        if (Array.isArray(value)) continue;

        const fieldPath = prefix ? `${prefix}.${key}` : key;

        if (value && typeof value === "object" && value !== null) {
          fields.push(fieldPath);
          const nested = extractFields(value, fieldPath);
          fields.push(...nested);
        } else {
          fields.push(fieldPath);
        }
      }
      return fields;
    };

    const getSingularForm = (word) => {
      if (!word) return "item";
      if (word.length <= 1 || !word.endsWith("s")) return word;

      const pluralPatterns = [
        { pattern: /ies$/, replacement: "y" },
        { pattern: /ves$/, replacement: "fe" },
        { pattern: /xes$/, replacement: "x" },
        { pattern: /ses$/, replacement: "s" },
        { pattern: /ches$/, replacement: "ch" },
        { pattern: /shes$/, replacement: "sh" },
        { pattern: /zes$/, replacement: "ze" },
        { pattern: /es$/, replacement: "" },
      ];

      for (const rule of pluralPatterns) {
        if (rule.pattern.test(word)) {
          const result = word.replace(rule.pattern, rule.replacement);
          if (result && result !== word) return result;
        }
      }

      const result = word.slice(0, -1);
      if (result.length < 1) return word;
      return result;
    };

    /**
     * Look up an array in `obj` whose key's singular form matches
     * `singularLoopVar`. Searches the top level first, then one level
     * deep into nested non-array objects.
     *
     * Returns `{ array, key }` on hit, or `null` on miss.
     */
    const findLoopArray = (obj, singularLoopVar) => {
      if (!obj || typeof obj !== "object") return null;

      // Pass 1: direct children.
      for (const [key, value] of Object.entries(obj)) {
        if (
          Array.isArray(value) &&
          value.length > 0 &&
          typeof value[0] === "object"
        ) {
          if (getSingularForm(key) === singularLoopVar) {
            return { array: value, key };
          }
        }
      }

      // Pass 2: one level deeper, so that something like
      // { wrapper: { rooms: [...] } } still resolves.
      for (const [key, value] of Object.entries(obj)) {
        if (
          value &&
          typeof value === "object" &&
          !Array.isArray(value) &&
          value !== null
        ) {
          for (const [innerKey, innerVal] of Object.entries(value)) {
            if (
              Array.isArray(innerVal) &&
              innerVal.length > 0 &&
              typeof innerVal[0] === "object"
            ) {
              if (getSingularForm(innerKey) === singularLoopVar) {
                return { array: innerVal, key: innerKey };
              }
            }
          }
        }
      }

      return null;
    };

    const result = [];
    const seen = new Set();
    const pushUnique = (fields) => {
      for (const f of fields) {
        if (!seen.has(f)) {
          seen.add(f);
          result.push(f);
        }
      }
    };

    // ============================================================
    // ⭐ Unwrap root array ONCE at the top.
    //
    // Whether `sample` is a root array (Case 2) or an object
    // (Case 3), the interesting structure lives in a single outer
    // object. We call that object `root` and work with it uniformly
    // from here on.
    // ============================================================
    const root = Array.isArray(sample) ? sample[0] : sample;
    if (!root || typeof root !== "object") return [];

    // ================================================================
    // 1. item.*
    //
    //    Look up the array whose singular-form key matches the loop
    //    var against `root`. If found, that array's first item is the
    //    current row.
    //
    //    If NOT found AND the sample is an OBJECT root (Case 3), the
    //    loop var names an outer loop bound to `root` itself — e.g.
    //    `{% for item in response_data %}` when response_data is an
    //    object. In that fallback, item.* = root's fields.
    //
    //    If the sample is a ROOT ARRAY (Case 1 / Case 2), the outer
    //    loop IS the screen maker — its "current row" is exactly what
    //    the client exposes at runtime as `response_data`, not as
    //    `item`. So we deliberately do NOT emit item.* for the
    //    outer-loop-bound-to-root case when the sample is an array.
    //    The response_data.* block below already covers those fields.
    // ================================================================
    if (loopVar) {
      let rowSample = null;

      const hit = findLoopArray(root, loopVar);
      if (hit && hit.array.length > 0 && typeof hit.array[0] === "object") {
        rowSample = hit.array[0];
      } else if (!Array.isArray(sample)) {
        rowSample = root;
      }

      if (rowSample) {
        pushUnique(extractFields(rowSample, "item"));
      }
    }

    // ================================================================
    // 2. response_data.* — the outer object's non-array fields.
    // ================================================================
    pushUnique(extractFields(root, "response_data"));

    return result;
  };

  const computedAvailableResponseFields = useMemo(() => {
    if (!parsedSample) return [];

    if (selectedButtonText && templateLines.length > 0) {
      const buttonRowIndex = templateLines.findIndex(
        (line) =>
          (line.type === "button" || line.isButton) &&
          (line.buttonText || line.content || "").trim() ===
          selectedButtonText.trim(),
      );

      if (buttonRowIndex !== -1) {
        const loopVar = findEnclosingLoopVar(buttonRowIndex, templateLines);
        return extractFieldsForContext(parsedSample, loopVar);
      }
    }

    return extractFieldsForContext(parsedSample, null);
  }, [parsedSample, selectedButtonText, templateLines]);

  const selectedButtonLoopContext = useMemo(() => {
    if (!selectedButtonText || templateLines.length === 0) return null;

    const buttonRowIndex = templateLines.findIndex(
      (line) =>
        (line.type === "button" || line.isButton) &&
        (line.buttonText || line.content || "").trim() ===
        selectedButtonText.trim(),
    );

    if (buttonRowIndex === -1) return null;

    return findEnclosingLoopVar(buttonRowIndex, templateLines);
  }, [selectedButtonText, templateLines]);

  const selectedSkill = useMemo(() => {
    if (selectedActionType !== "skill") return null;
    if (!selectedIntentType) return null;
    return intents.find((it) => it.intent_type === selectedIntentType) || null;
  }, [selectedActionType, selectedIntentType, intents]);

  const selectedSkillFields = useMemo(() => {
    if (!selectedSkill) return [];
    return Array.isArray(selectedSkill.required_fields)
      ? selectedSkill.required_fields
      : [];
  }, [selectedSkill]);

  const isCardViewActive = () => {
    if (!transformationRules || transformationRules.length === 0) return false;
    return transformationRules.some((rule) => {
      if (rule.operation === "cards" || rule.type === "card") return true;
      if (rule.transformedText) {
        const html =
          typeof rule.transformedText === "string" ? rule.transformedText : "";
        if (
          html.includes("previewCard") ||
          html.includes("previewCardContainer")
        )
          return true;
      }
      if (rule.selectors && Array.isArray(rule.selectors)) {
        return rule.selectors.some((s) => {
          const start = s.start?.elementPath || s.start || "";
          const end = s.end?.elementPath || s.end || "";
          return start.includes("previewCard") || end.includes("previewCard");
        });
      }
      return false;
    });
  };

  useEffect(() => {
    if (!selectedActionId || selectedActionType !== "api_config") {
      setSelectedApiConfigDetails(null);
      return;
    }

    const config = availableApiConfigs.find(
      (c) => c.api_config_id === selectedActionId,
    );
    setSelectedApiConfigDetails(config || null);
    console.log("[Controls] 📋 Selected API config details:", config);
  }, [selectedActionId, selectedActionType, availableApiConfigs]);

  useEffect(() => {
    if (showTriggerModal) {
      fetchAvailableActions();
    }
  }, [showTriggerModal]);

  useEffect(() => {
    if (!showTriggerModal) return;
    if (selectedActionType !== "skill") return;
    if (!tenantId || !kbId) return;

    const realTenantId = meData?.tenant_info?.id;
    if (!realTenantId) return;

    let cancelled = false;

    const loadIntents = async () => {
      setLoadingIntents(true);
      setIntentsError(null);
      try {
        const list = await fetchIntents({ tenantId, kbId, region });
        if (!cancelled) setIntents(list);
      } catch (err) {
        if (!cancelled) setIntentsError(err.message || "Failed to load skills");
      } finally {
        if (!cancelled) setLoadingIntents(false);
      }
    };

    loadIntents();
    return () => {
      cancelled = true;
    };
  }, [showTriggerModal, selectedActionType, tenantId, kbId, region, meData]);

  const fetchAvailableActions = async () => {
    setLoadingActions(true);
    setActionError(null);

    try {
      const region = process.env.REACT_APP_REGION || "us";
      const token = localStorage.getItem("token") || "";
      const stackPrefix = process.env.REACT_APP_STAGE_ENV || "dev";
      const domainBase = process.env.REACT_APP_SL_BE_BASE_ENV_PART;

      const apiConfigsUrl = `https://${region}.autobot2.${domainBase}/${stackPrefix}/api_config`;
      const apiConfigsResponse = await fetch(apiConfigsUrl, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });

      if (apiConfigsResponse.ok) {
        const data = await apiConfigsResponse.json();
        const allConfigs = data.configs || [];
        // This dropdown only wires REGULAR configs to button triggers. Auth
        // configs (PRIMARY / SECONDARY_1 / SECONDARY_2) are managed in the
        // Authentication section and must never appear as trigger targets.
        const regularConfigs = allConfigs.filter(
          (cfg) => cfg.api_type === "REGULAR",
        );
        setAvailableApiConfigs(regularConfigs);
        console.log("[Controls] 📋 Available API configs:", regularConfigs);
      }

      const chainsUrl = `https://${region}.autobot2.${domainBase}/${stackPrefix}/chain`;
      const chainsResponse = await fetch(chainsUrl, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });

      if (chainsResponse.ok) {
        const data = await chainsResponse.json();
        setAvailableChains(data.chains || []);
        console.log("[Controls] 📋 Available chains:", data.chains);
      }
    } catch (error) {
      console.error("[Controls] ❌ Error fetching actions:", error);
      setActionError("Failed to load available actions");
    } finally {
      setLoadingActions(false);
    }
  };

  const [mappingValidity, setMappingValidity] = useState(true);

  const handleMappingChange = useCallback((newMappings, allValid = true) => {
    setResponseToRequestMapping((prev) => {
      const pk = Object.keys(prev || {});
      const nk = Object.keys(newMappings || {});
      if (pk.length === nk.length) {
        let same = true;
        outer: for (const k of pk) {
          const pa = prev[k] || {};
          const na = newMappings[k] || {};
          const pak = Object.keys(pa);
          const nak = Object.keys(na);
          if (pak.length !== nak.length) {
            same = false;
            break outer;
          }
          for (const kk of pak) {
            if ((pa[kk] || "") !== (na[kk] || "")) {
              same = false;
              break outer;
            }
          }
        }
        if (same) return prev;
      }
      return newMappings;
    });
    setMappingValidity((prev) => (prev === allValid ? prev : allValid));
  }, []);

  // ⭐ Value-guarded handler for SkillFieldsMapper
  const handleIntentMappingChange = useCallback(
    (newMappings, allValid = true) => {
      setIntentFieldMapping((prev) => {
        const pk = Object.keys(prev || {});
        const nk = Object.keys(newMappings || {});
        if (pk.length === nk.length) {
          let same = true;
          for (const k of nk) {
            if ((prev?.[k] || "") !== (newMappings?.[k] || "")) {
              same = false;
              break;
            }
          }
          if (same) return prev;
        }
        return newMappings;
      });

      setIntentMappingValidity((prev) => (prev === allValid ? prev : allValid));
    },
    [],
  );

  const handleEditTrigger = (action) => {
    console.log("[Controls] ✏️ Editing trigger:", action);
    setEditingActionId(action.id);
    setSelectedButtonText(action.buttonText);
    setSelectedActionType(action.actionType || "api_config");
    setSelectedActionId(action.actionId || "");
    setSelectedIntentType(action.intent_type || "");

    if (action.actionType === "skill" && action.intent_field_mapping) {
      setIntentFieldMapping(action.intent_field_mapping);
      setIntentMappingValidity(true);
    } else {
      setIntentFieldMapping({});
      setIntentMappingValidity(true);
    }

    if (action.response_obj_to_req_fields_mapping) {
      setResponseToRequestMapping(action.response_obj_to_req_fields_mapping);
      setMappingValidity(true);
    } else {
      setResponseToRequestMapping({});
      setMappingValidity(true);
    }

    setShowTriggerModal(true);
  };

  const handleAddTrigger = () => {
    if (!selectedButtonText) {
      setActionError("Please select a button");
      return;
    }

    if (selectedActionType === "skill") {
      if (!selectedIntentType) {
        setActionError("Please select a skill");
        return;
      }
      // Skill field mapping is OPTIONAL. Only reject malformed templates.
      if (!intentMappingValidity) {
        setActionError(
          "One or more skill field mappings are invalid. Fix them before saving.",
        );
        return;
      }
    } else {
      if (!selectedActionId) {
        setActionError("Please select an action (API config or Chain)");
        return;
      }
    }

    if (selectedActionType === "api_config" && !mappingValidity) {
      setActionError(
        "One or more field mappings are invalid. Fix them before saving.",
      );
      return;
    }

    if (selectedActionType === "api_config" && selectedApiConfigDetails) {
      const apiCfg = selectedApiConfigDetails.api_config || {};
      const requiredPlaceholders = new Set();

      const url = apiCfg.url || "";
      const pathMatches = url.match(/\{\{\s*([^}]+)\s*\}\}/g);
      if (pathMatches) {
        pathMatches.forEach((m) => {
          const name = m.replace(/[\{\}\s]/g, "");
          requiredPlaceholders.add(name);
        });
      }

      const queryParams = apiCfg.query_params || {};
      Object.keys(queryParams).forEach((key) => {
        const value = queryParams[key];
        if (typeof value === "string") {
          const matches = value.match(/\{\{\s*([^}]+)\s*\}\}/g);
          if (matches) {
            matches.forEach((m) => {
              const name = m.replace(/[\{\}\s]/g, "");
              requiredPlaceholders.add(name);
            });
          }
        }
      });

      const payloadTemplate = apiCfg.payload_template || "{}";
      const payloadMatches = payloadTemplate.match(/\{\{\s*([^}]+)\s*\}\}/g);
      if (payloadMatches) {
        payloadMatches.forEach((m) => {
          const name = m.replace(/[\{\}\s]/g, "");
          requiredPlaceholders.add(name);
        });
      }

      const allMappings = Object.values(responseToRequestMapping).reduce(
        (acc, typeMappings) => [...acc, ...Object.values(typeMappings)],
        [],
      );
      const mappedValues = allMappings.filter((v) => v && v.trim() !== "");
      const unmappedCount = requiredPlaceholders.size - mappedValues.length;

      if (unmappedCount > 0) {
        setActionError(
          `Please map all required fields. ${unmappedCount} placeholder(s) not yet assigned.`,
        );
        return;
      }
    }

    const selectedButton = availableButtons.find(
      (b) => b.text === selectedButtonText,
    );

    const newAction = {
      id:
        editingActionId ||
        `trigger_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      buttonText: selectedButtonText,
      buttonId: selectedButton?.id || selectedButtonText,
      actionType: selectedActionType,
      actionId:
        selectedActionType === "skill" ? selectedIntentType : selectedActionId,
    };

    if (selectedActionType === "skill") {
      newAction.intent_type = selectedIntentType;

      // ============================================================
      // ⭐ NEW: Persist skill field mappings in the extended object
      //        shape: { value, prefill, read_only, display }.
      //
      //        Legacy string entries are up-converted on the fly so
      //        any in-flight editor session that somehow holds a
      //        plain string still saves cleanly.
      //
      //        Only entries whose `value` is non-empty are kept —
      //        a user who prefills nothing gets a trigger with no
      //        intent_field_mapping at all.
      // ============================================================
      const cleanedMapping = {};
      for (const [key, entry] of Object.entries(intentFieldMapping || {})) {
        let normalized;

        if (typeof entry === "string") {
          // Legacy string form → wrap with defaults.
          normalized = {
            value: entry,
            prefill: true,
            read_only: false,
            display: true,
          };
        } else if (entry && typeof entry === "object") {
          // New object form → normalize each flag with a default.
          normalized = {
            value: typeof entry.value === "string" ? entry.value : "",
            prefill: entry.prefill !== undefined ? !!entry.prefill : true,
            read_only: entry.read_only !== undefined ? !!entry.read_only : false,
            display: entry.display !== undefined ? !!entry.display : true,
          };
        } else {
          continue;
        }

        // Only persist entries that actually carry a value. An entry
        // with an empty value can't prefill, can't be locked
        // meaningfully, and can't be hidden — it's a no-op.
        if (normalized.value.trim() === "") continue;

        cleanedMapping[key] = normalized;
      }

      if (Object.keys(cleanedMapping).length > 0) {
        newAction.intent_field_mapping = cleanedMapping;
      }
    }

    if (selectedButtonLoopContext) {
      newAction.loopVariable = selectedButtonLoopContext;
    }

    if (
      selectedActionType === "api_config" &&
      Object.keys(responseToRequestMapping).length > 0
    ) {
      const hasMappings = Object.values(responseToRequestMapping).some(
        (typeMappings) => Object.keys(typeMappings).length > 0,
      );

      if (hasMappings) {
        newAction.response_obj_to_req_fields_mapping = responseToRequestMapping;
        console.log(
          "[Controls] 📋 Including mapping in trigger:",
          responseToRequestMapping,
        );
      }
    }

    if (editingActionId) {
      console.log("[Controls] ✏️ Updating trigger:", newAction);
      if (onUpdateAction) {
        onUpdateAction(newAction);
      }
    } else {
      console.log("[Controls] ➕ Adding trigger:", newAction);
      if (onAddAction) {
        onAddAction(newAction);
      }
    }

    resetTriggerModal();
    setShowTriggerModal(false);
    setEditingActionId(null);
  };

  const resetTriggerModal = () => {
    setSelectedButtonText("");
    setSelectedActionType("api_config");
    setSelectedActionId("");
    setSelectedIntentType("");
    setIntentFieldMapping({});
    setIntentMappingValidity(true);
    setActionError(null);
    setResponseToRequestMapping({});
    setSelectedApiConfigDetails(null);
    setEditingActionId(null);
    setMappingValidity(true);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowTriggerModal(false);
        resetTriggerModal();
      }
    };
    if (showTriggerModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showTriggerModal]);

  // ============================================================
  // findTemplateRowIndex — unchanged
  // ============================================================
  const findTemplateRowIndex = (step) => {
    console.log("templateLinesdddddddd", templateLines);

    if (isButtonGroupStep(step)) {
      const groupName = step.groupName || "";
      if (groupName) {
        const idx = templateLines.findIndex(
          (line) =>
            (line.type === "button-group" || line.isButtonGroup) &&
            (line.content || "").trim() === groupName,
        );
        if (idx !== -1) return idx;
      }
      return -1;
    }

    if (isButtonStep(step)) {
      const buttonText = step.buttonText || "";
      if (buttonText) {
        const idx = templateLines.findIndex(
          (line) =>
            (line.type === "button" || line.isButton) &&
            (line.buttonText || line.content || "").trim() === buttonText,
        );
        if (idx !== -1) return idx;
      }
      return -1;
    }

    if (step.isButtonGroupTransformation) {
      const groupName =
        step._metadata?.groupName || step.groupName || step.originalText || "";
      if (groupName) {
        const idx = templateLines.findIndex(
          (line) =>
            (line.type === "button-group" || line.isButtonGroup) &&
            (line.content || "").trim() === groupName,
        );
        if (idx !== -1) return idx;
      }
      return -1;
    }

    if (step.isButtonTransformation) {
      const buttonText =
        step._metadata?.buttonText ||
        step.originalText ||
        (step.fields && step.fields[0]) ||
        "";
      if (buttonText) {
        const idx = templateLines.findIndex(
          (line) =>
            (line.type === "button" || line.isButton) &&
            (line.buttonText || line.content || "").trim() === buttonText,
        );
        if (idx !== -1) return idx;
      }
      return -1;
    }

    return -1;
  };

  // ============================================================
  // convertToMinimalSchema — unchanged
  // ============================================================
  const convertToMinimalSchema = (rules) => {
    return rules.map((rule) => {
      if (rule.type === "button-group" || rule.isButtonGroupTransformation) {
        const cleanSelectorsList = cleanSelectors(
          rule.selectors || [
            {
              start:
                rule.groupLocation?.selector ||
                rule._metadata?.selectorPath ||
                "",
              end:
                rule.groupLocation?.selector ||
                rule._metadata?.selectorPath ||
                "",
            },
          ],
        );

        let buttons = rule.buttons || [];
        if (buttons.length > 0 && typeof buttons[0] === "object") {
          buttons = buttons.map((b) => b.text || String(b));
        }

        return {
          id: rule.id,
          type: "button-group",
          operation: "isButtonGroup",
          groupName:
            rule.groupName || rule._metadata?.groupName || "Button Group",
          buttons: buttons,
          selectors: cleanSelectorsList,
          timestamp: rule.timestamp || new Date().toISOString(),
        };
      }

      if (rule.type === "button" || rule.isButtonTransformation) {
        const cleanSelectorsList = cleanSelectors(rule.selectors || []);

        let buttonText =
          rule.buttonText ||
          rule._metadata?.buttonText ||
          (rule.fields && rule.fields[0]) ||
          "";

        return {
          id: rule.id,
          type: "button",
          operation: "isButton",
          buttonText: buttonText,
          selectors: cleanSelectorsList,
          timestamp: rule.timestamp || new Date().toISOString(),
        };
      }

      if (rule.type === "combined" && rule.phases) {
        const cleanSelectorsList = cleanSelectors(rule.selectors || []);

        const cleanedPhases = {
          structural: {
            operations: (rule.phases.structural?.operations || []).map(
              (op) => ({
                operation: op.operation,
                fields: op.fields || [],
              }),
            ),
          },
          media: {
            operations: (rule.phases.media?.operations || []).map((op) => ({
              operation: op.operation,
              fields: op.fields || [],
              ...(op.imageSize ? { imageSize: op.imageSize } : {}),
            })),
          },
          styles: {
            operations: (rule.phases.styles?.operations || []).map((op) => ({
              operation: op.operation,
              fields: op.fields || [],
              ...(op.style_meta ? { style_meta: op.style_meta } : {}),
              ...(op.operations ? { operations: op.operations } : {}),
            })),
          },
        };

        return {
          id: rule.id,
          type: "combined",
          operation: "combined",
          selectors: cleanSelectorsList,
          phases: cleanedPhases,
          timestamp: rule.timestamp || new Date().toISOString(),
        };
      }

      if (rule.type === "style") {
        const cleanSelectorsList = cleanSelectors(rule.selectors || []);

        let operations = [];
        if (
          rule.operations &&
          Array.isArray(rule.operations) &&
          rule.operations.length > 0
        ) {
          operations = rule.operations;
        } else if (rule.operation) {
          operations = [rule.operation];
        } else {
          operations = ["highlight"];
        }

        const style_meta = rule.style_meta || {
          full_row: rule.fullRow || rule.isFullRow || false,
          alignment: rule.alignment || "left",
        };

        return {
          id: rule.id || `style_${Date.now()}`,
          type: "style",
          operation: operations.length === 1 ? operations[0] : "combined",
          operations: operations,
          selectors: cleanSelectorsList,
          fields: rule.fields || [],
          style_meta: style_meta,
          timestamp: rule.timestamp || new Date().toISOString(),
        };
      }

      if (rule.type === "structural") {
        const cleanSelectorsList = cleanSelectors(rule.selectors || []);
        return {
          id: rule.id || `struct_${Date.now()}`,
          type: "structural",
          operation: rule.operation || "table",
          selectors: cleanSelectorsList,
          fields: rule.fields || [],
          timestamp: rule.timestamp || new Date().toISOString(),
        };
      }

      if (rule.type === "media") {
        const cleanSelectorsList = cleanSelectors(rule.selectors || []);
        const step = {
          id: rule.id || `media_${Date.now()}`,
          type: "media",
          operation: "images",
          selectors: cleanSelectorsList,
          fields: rule.fields || [],
          timestamp: rule.timestamp || new Date().toISOString(),
        };

        if (rule.imageWidth || rule.imageHeight) {
          step.imageSize = {
            width: rule.imageWidth || 250,
            height: rule.imageHeight || 250,
          };
        }

        return step;
      }

      const cleanSelectorsList = cleanSelectors(rule.selectors || []);

      let operations = [];
      if (rule.operations && Array.isArray(rule.operations)) {
        operations = rule.operations;
      } else if (rule.operation) {
        operations = [rule.operation];
      } else {
        operations = ["highlight"];
      }

      const style_meta = rule.style_meta || {
        full_row: rule.fullRow || rule.isFullRow || false,
        alignment: rule.alignment || "left",
      };

      return {
        id: rule.id || `trans_${Date.now()}`,
        type: rule.type || "style",
        operation: operations.length === 1 ? operations[0] : "combined",
        operations: operations,
        selectors: cleanSelectorsList,
        fields: rule.fields || [],
        style_meta: style_meta,
        timestamp: rule.timestamp || new Date().toISOString(),
      };
    });
  };

  const handleSave = async () => {
    if (safeTemplate.trim()) {
      const validation = TemplateValidator.validateForCase(
        safeTemplate,
        caseType,
      );

      if (!validation.valid) {
        setSaveStatus({
          type: "error",
          message: `Cannot save: ${validation.errors[0].message}`,
        });
        return;
      }
    }

    setIsSaving(true);
    setSaveStatus(null);

    const controlsState = {
      show_button: true,
      button_text: "Continue",
      theme: "dark",
      scroll_mode: scrollMode,
      layout_direction: layoutDirection,
      data_type: dataType,
      is_sample_array: isSampleArray,
      array_layout_direction: arrayLayoutDirection,
    };

    const responseTemplate = safeTemplate || "";

    let respTemplateToSave = responseTemplate;
    try {
      const parsed = JSON.parse(responseTemplate);
      if (typeof parsed === "object" && parsed !== null) {
        respTemplateToSave = parsed;
        console.log("📦 Template parsed as JSON object:", parsed);
      } else {
        respTemplateToSave = responseTemplate;
      }
    } catch (e) {
      respTemplateToSave = responseTemplate;
      console.log("📝 Template saved as raw string (contains Jinja/Nunjucks)");
    }

    const minimalSteps = convertToMinimalSchema(transformationRules);
    console.log("[Controls] 📊 Minimal steps:", minimalSteps);

    const stepsWithRowIndex = minimalSteps.map((step) => {
      const rowIdx = findTemplateRowIndex(step);

      if (isButtonGroupStep(step)) {
        return { ...step, _templateRowIndex: rowIdx };
      }

      if (isButtonStep(step)) {
        return { ...step, _templateRowIndex: rowIdx };
      }

      return step;
    });

    console.log("[Controls] 📊 Steps with row index:", stepsWithRowIndex);

    const requestBody = {
      config_id: configId,
      status_code: statusCode,
      kb_id: kbId,
      resp_template: respTemplateToSave,
      controls: controlsState,
      actions: actions,
      transformation_steps: stepsWithRowIndex,
    };

    console.log("📤 Request body:", JSON.stringify(requestBody, null, 2));

    if (modalMode) {
      console.log("[Controls] 🔄 Modal mode - returning data without API call");

      const result = {
        success: true,
        id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        data: requestBody,
        message: "Template data ready (modal mode)",
      };

      setSaveStatus({
        type: "success",
        message:
          '✅ Template designed successfully! Click "Use Template" to apply.',
      });

      if (onTemplateDataReady) {
        onTemplateDataReady({
          ...requestBody,
          templateString: respTemplateToSave,
          controls: controlsState,
          transformation_steps: stepsWithRowIndex,
        });
      }

      if (onSaveSuccess) {
        onSaveSuccess(result);
      }

      setIsSaving(false);
      return;
    }

    try {
      const template_url = queryId
        ? `https://us.autobot2.${backend_api_base_url}/final_response_update`
        : `https://us.autobot2.${backend_api_base_url}/final_response`;
      const method = queryId ? "PUT" : "POST";
      if (queryId) {
        requestBody.id = queryId;
      }
      const response = await fetch(template_url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (response.ok) {
        console.log("✅ API Response:", responseData);
        setSaveStatus({
          type: "success",
          message: `✅ Response configuration created: ${responseData.id || "Success"}`,
        });

        if (onSaveSuccess) {
          onSaveSuccess(responseData);
        }
      } else {
        console.error("❌ API Error:", responseData);
        setSaveStatus({
          type: "error",
          message: `❌ Error: ${responseData.error || responseData.message || "Failed to save"}`,
        });
      }
    } catch (error) {
      console.error("❌ Network Error:", error);
      setSaveStatus({
        type: "error",
        message: `❌ Network Error: ${error.message}`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getActionLabel = (type, id) => {
    if (type === "api_config") {
      const config = availableApiConfigs.find((c) => c.api_config_id === id);
      if (config) {
        const url = config.api_config?.url || "No URL";
        return `API: ${url.substring(0, 30)}${url.length > 30 ? "..." : ""}`;
      }
      return `API Config: ${id.substring(0, 12)}...`;
    } else if (type === "chain") {
      const chain = availableChains.find((c) => c.chain_id === id);
      if (chain) {
        return `Chain: ${chain.name || id.substring(0, 12)}...`;
      }
      return `Chain: ${id.substring(0, 12)}...`;
    }
    return id;
  };

  const getTransformationTypeLabel = (step) => {
    const type = step?.type || "style";
    const labels = {
      combined: "📋 Combined",
      style: "🎨 Style",
      structural: "📐 Structural",
      media: "🖼️ Media",
      button: "🔘 Button",
      "button-group": "📦 Button Group",
    };
    return labels[type] || type || "Unknown";
  };

  const getTransformationTypeColor = (step) => {
    const type = step?.type || "style";
    const colors = {
      combined: "#2b6cb0",
      style: "#48bb78",
      structural: "#4299e1",
      media: "#8b5e3c",
      button: "#d69e2e",
      "button-group": "#ed8936",
    };
    return colors[type] || "#a0aec0";
  };

  const getTransformationTypeBg = (step) => {
    const type = step?.type || "style";
    const bgColors = {
      combined: "#ebf8ff",
      style: "#f0fff4",
      structural: "#ebf8ff",
      media: "#faf0e6",
      button: "#fefcbf",
      "button-group": "#fefcbf",
    };
    return bgColors[type] || "#edf2f7";
  };

  const getTransformationIcon = (step) => {
    const type = step?.type || "style";
    const icons = {
      combined: "📋",
      style: "🎨",
      structural: "📐",
      media: "🖼️",
      button: "🔘",
      "button-group": "📦",
    };
    return icons[type] || "";
  };

  const getCombinedStepPhaseCounts = (step) => {
    if (!step?.phases) return null;

    const counts = {
      structural: step.phases.structural?.operations?.length || 0,
      media: step.phases.media?.operations?.length || 0,
      styles: step.phases.styles?.operations?.length || 0,
    };

    const total = counts.structural + counts.media + counts.styles;

    return {
      total,
      counts,
      display: `📐${counts.structural} 🖼️${counts.media} 🎨${counts.styles}`,
    };
  };

  const getStepDisplay = (step) => {
    if (!step)
      return { label: "Unknown", icon: "📝", color: "#a0aec0", bg: "#edf2f7" };

    return {
      label: getTransformationTypeLabel(step),
      icon: getTransformationIcon(step),
      color: getTransformationTypeColor(step),
      bg: getTransformationTypeBg(step),
    };
  };

  const isActionTypeSelectionMissing = (() => {
    if (selectedActionType === "skill") return !selectedIntentType;
    return !selectedActionId;
  })();

  const isAddTriggerDisabled =
    !selectedButtonText ||
    isActionTypeSelectionMissing ||
    (selectedActionType === "api_config" && !mappingValidity) ||
    (selectedActionType === "skill" && !intentMappingValidity);

  return (
    <div className={styles.controlsContainer}>
      {isCardViewActive() && (
        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Card Scroll Direction:</span>
          <button
            className={`${styles.controlButton} ${scrollMode === "vertical" ? styles.activeControl : ""}`}
            onClick={() => setScrollMode("vertical")}
          >
            Vertical
          </button>
          <button
            className={`${styles.controlButton} ${scrollMode === "horizontal" ? styles.activeControl : ""}`}
            onClick={() => setScrollMode("horizontal")}
          >
            Horizontal
          </button>
        </div>
      )}

      {showDirectionControl && (
        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Direction:</span>
          <button
            className={`${styles.controlButton} ${arrayLayoutDirection === "vertical" ? styles.activeControl : ""}`}
            onClick={() => setArrayLayoutDirection("vertical")}
          >
            Vertical
          </button>
          <button
            className={`${styles.controlButton} ${arrayLayoutDirection === "horizontal" ? styles.activeControl : ""}`}
            onClick={() => setArrayLayoutDirection("horizontal")}
          >
            Horizontal
          </button>
        </div>
      )}

      {dataType === "array" && !isSampleArray && (
        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Layout:</span>
          <button
            className={`${styles.controlButton} ${layoutDirection === "vertical" ? styles.activeControl : ""}`}
            onClick={() => setLayoutDirection("vertical")}
          >
            Vertical
          </button>
          <button
            className={`${styles.controlButton} ${layoutDirection === "horizontal" ? styles.activeControl : ""}`}
            onClick={() => setLayoutDirection("horizontal")}
          >
            Horizontal
          </button>
        </div>
      )}

      {/* Transformation rules summary */}
      {transformationRules.length > 0 && (
        <div
          className={styles.controlGroup}
          style={{
            marginTop: "12px",
            borderTop: "2px solid #e2e8f0",
            paddingTop: "12px",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: "600",
              color: "#2d3748",
              marginBottom: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>📋 Transformation Steps ({transformationRules.length})</span>
            <span
              style={{
                fontSize: "10px",
                fontWeight: "400",
                color: "#718096",
              }}
            >
              {transformationRules.some((r) => r.type === "combined") &&
                "✨ Includes combined steps"}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              maxHeight: "300px",
              overflow: "auto",
            }}
          >
            {transformationRules.map((step, index) => {
              if (isButtonStep(step) || step.type === "button") {
                return (
                  <div
                    key={step.id || index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "4px 8px",
                      backgroundColor: "#fefcbf",
                      borderRadius: "4px",
                      border: "1px solid #f6ad55",
                      fontSize: "11px",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ fontWeight: "600", color: "#4a5568" }}>
                      #{index + 1}
                    </span>
                    <span
                      style={{
                        padding: "1px 8px",
                        borderRadius: "10px",
                        fontSize: "9px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        backgroundColor: "#fefcbf",
                        color: "#d69e2e",
                      }}
                    >
                      🔘 BUTTON
                    </span>
                    <span style={{ fontWeight: "500", color: "#2d3748" }}>
                      {step.buttonText || step.fields?.[0] || "Button"}
                    </span>
                  </div>
                );
              }

              if (isButtonGroupStep(step) || step.type === "button-group") {
                return (
                  <div
                    key={step.id || index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "4px 8px",
                      backgroundColor: "#fefcbf",
                      borderRadius: "4px",
                      border: "1px solid #ed8936",
                      fontSize: "11px",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ fontWeight: "600", color: "#4a5568" }}>
                      #{index + 1}
                    </span>
                    <span
                      style={{
                        padding: "1px 8px",
                        borderRadius: "10px",
                        fontSize: "9px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        backgroundColor: "#fefcbf",
                        color: "#ed8936",
                      }}
                    >
                      📦 GROUP
                    </span>
                    <span style={{ fontWeight: "500", color: "#2d3748" }}>
                      {step.groupName || "Button Group"}
                    </span>
                    <span style={{ fontSize: "10px", color: "#718096" }}>
                      ({step.buttons?.length || 0} buttons)
                    </span>
                  </div>
                );
              }

              if (isCombinedStep(step) || step.type === "combined") {
                const phaseCounts = getCombinedStepPhaseCounts(step);
                const totalOps = phaseCounts?.total || 0;

                return (
                  <div
                    key={step.id || index}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      padding: "6px 10px",
                      backgroundColor: "#ebf8ff",
                      borderRadius: "6px",
                      border: "2px solid #2b6cb0",
                      fontSize: "11px",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#2b6cb0",
                          minWidth: "24px",
                        }}
                      >
                        #{index + 1}
                      </span>
                      <span
                        style={{
                          padding: "1px 8px",
                          borderRadius: "10px",
                          fontSize: "9px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          backgroundColor: "#bee3f8",
                          color: "#2b6cb0",
                        }}
                      >
                        📋 COMBINED
                      </span>
                      <span style={{ fontWeight: "600", color: "#2b6cb0" }}>
                        {totalOps} operations
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        paddingLeft: "32px",
                        fontSize: "10px",
                        color: "#4a5568",
                      }}
                    >
                      <span>📐 {phaseCounts?.counts?.structural || 0}</span>
                      <span>🖼️ {phaseCounts?.counts?.media || 0}</span>
                      <span>🎨 {phaseCounts?.counts?.styles || 0}</span>
                      <span style={{ color: "#a0aec0" }}>
                        {step.timestamp
                          ? new Date(step.timestamp).toLocaleTimeString()
                          : ""}
                      </span>
                    </div>
                  </div>
                );
              }

              const typeLabel = getTransformationTypeLabel(step);
              const typeColor = getTransformationTypeColor(step);
              const typeBg = getTransformationTypeBg(step);
              const icon = getTransformationIcon(step);
              const isStyle = step.type === "style";
              const isStructural = step.type === "structural";
              const isMedia = step.type === "media";

              return (
                <div
                  key={step.id || index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "4px 8px",
                    backgroundColor: "#f7fafc",
                    borderRadius: "4px",
                    border: `1px solid ${isStructural ? "#bee3f8" : isMedia ? "#f0d5c4" : "#e2e8f0"}`,
                    fontSize: "11px",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontWeight: "600", color: "#4a5568" }}>
                    #{index + 1}
                  </span>
                  <span
                    style={{
                      padding: "1px 8px",
                      borderRadius: "10px",
                      fontSize: "9px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      backgroundColor: typeBg,
                      color: typeColor,
                    }}
                  >
                    {icon} {typeLabel}
                  </span>
                  <span style={{ fontWeight: "500", color: "#2d3748" }}>
                    {step.operation}
                  </span>
                  {step.fields && step.fields.length > 0 && (
                    <span
                      style={{
                        color: "#718096",
                        fontSize: "10px",
                        fontFamily: "monospace",
                        maxWidth: "120px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {Array.isArray(step.fields)
                        ? step.fields.join(", ")
                        : step.fields}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ TRIGGERS SECTION ============ */}
      <div
        className={styles.controlGroup}
        style={{
          marginTop: "12px",
          borderTop: "2px solid #e2e8f0",
          paddingTop: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <span
            style={{ fontSize: "12px", fontWeight: "600", color: "#2d3748" }}
          >
            ⚡ Triggers
          </span>
          <button
            onClick={() => {
              setEditingActionId(null);
              setSelectedButtonText("");
              setSelectedActionType("api_config");
              setSelectedActionId("");
              setSelectedIntentType("");
              setIntentFieldMapping({});
              setIntentMappingValidity(true);
              setResponseToRequestMapping({});
              setMappingValidity(true);
              setShowTriggerModal(true);
            }}
            disabled={availableButtons.length === 0}
            style={{
              padding: "4px 12px",
              backgroundColor:
                availableButtons.length === 0 ? "#a0aec0" : "#4299e1",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: availableButtons.length === 0 ? "not-allowed" : "pointer",
              fontSize: "11px",
              fontWeight: "500",
            }}
            title={
              availableButtons.length === 0
                ? "Add a button in the template first"
                : ""
            }
          >
            + Add Trigger
          </button>
        </div>

        {availableButtons.length > 0 && (
          <div
            style={{
              fontSize: "10px",
              color: "#718096",
              marginBottom: "6px",
            }}
          >
            🔘 {availableButtons.length} button
            {availableButtons.length > 1 ? "s" : ""} available in template
          </div>
        )}

        {actions.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {actions.map((action) => (
              <div
                key={action.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#f7fafc",
                  padding: "6px 10px",
                  borderRadius: "4px",
                  border: "1px solid #e2e8f0",
                  fontSize: "11px",
                  gap: "6px",
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontWeight: "500", color: "#2d3748" }}>
                  🔘 {action.buttonText}
                </span>

                <span
                  style={{
                    fontSize: "10px",
                    color:
                      action.actionType === "skill"
                        ? "#805ad5"
                        : action.actionType === "api_config"
                          ? "#4299e1"
                          : "#48bb78",
                    backgroundColor:
                      action.actionType === "skill"
                        ? "#faf5ff"
                        : action.actionType === "api_config"
                          ? "#ebf8ff"
                          : "#f0fff4",
                    padding: "1px 8px",
                    borderRadius: "10px",
                  }}
                >
                  {action.actionType === "skill"
                    ? "Skill"
                    : action.actionType === "api_config"
                      ? "API"
                      : "Chain"}
                </span>

                {action.actionType === "skill" && action.intent_type && (
                  <span
                    style={{
                      fontSize: "9px",
                      color: "#805ad5",
                      backgroundColor: "#faf5ff",
                      padding: "1px 8px",
                      borderRadius: "10px",
                    }}
                  >
                    🎯 {action.intent_type}
                  </span>
                )}

                {action.actionType === "skill" &&
                  action.intent_field_mapping &&
                  Object.keys(action.intent_field_mapping).length > 0 && (
                    <span
                      style={{
                        fontSize: "9px",
                        color: "#9f7aea",
                        backgroundColor: "#faf5ff",
                        padding: "1px 8px",
                        borderRadius: "10px",
                      }}
                    >
                      🔄 Prefill
                    </span>
                  )}

                {action.loopVariable && (
                  <span
                    style={{
                      fontSize: "9px",
                      color: "#ed8936",
                      backgroundColor: "#fefcbf",
                      padding: "1px 8px",
                      borderRadius: "10px",
                    }}
                  >
                    ↻ {action.loopVariable}
                  </span>
                )}

                {action.response_obj_to_req_fields_mapping && (
                  <span
                    style={{
                      fontSize: "9px",
                      color: "#9f7aea",
                      backgroundColor: "#faf5ff",
                      padding: "1px 8px",
                      borderRadius: "10px",
                    }}
                  >
                    🔄 Mapped
                  </span>
                )}

                <div
                  style={{ display: "flex", gap: "4px", marginLeft: "auto" }}
                >
                  <button
                    onClick={() => handleEditTrigger(action)}
                    style={{
                      backgroundColor: "transparent",
                      border: "1px solid #4299e1",
                      color: "#4299e1",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "11px",
                      padding: "2px 8px",
                    }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => onRemoveAction && onRemoveAction(action.id)}
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                      color: "#fc8181",
                      cursor: "pointer",
                      fontSize: "14px",
                      padding: "0 4px",
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              fontSize: "11px",
              color: "#a0aec0",
              textAlign: "center",
              padding: "8px",
              backgroundColor: "#f7fafc",
              borderRadius: "4px",
              border: "1px dashed #e2e8f0",
            }}
          >
            {availableButtons.length === 0
              ? "No buttons found in template. Add a button first via the Template Editor."
              : "No triggers configured"}
          </div>
        )}
      </div>

      {/* ============ ADD/EDIT TRIGGER MODAL ============ */}
      {showTriggerModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            ref={modalRef}
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "650px",
              width: "90%",
              maxHeight: "85vh",
              overflow: "auto",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", color: "#2d3748" }}>
                {editingActionId ? "✏️ Edit Trigger" : "⚡ Add Trigger"}
              </h3>
              <button
                onClick={() => {
                  setShowTriggerModal(false);
                  resetTriggerModal();
                }}
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "#718096",
                }}
              >
                ✕
              </button>
            </div>

            {/* Button Selector */}
            <div style={{ marginBottom: "12px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#4a5568",
                  marginBottom: "4px",
                }}
              >
                Select Button <span style={{ color: "#e53e3e" }}>*</span>
              </label>
              {availableButtons.length === 0 ? (
                <div
                  style={{
                    padding: "10px 12px",
                    backgroundColor: "#fefcbf",
                    borderRadius: "4px",
                    border: "1px solid #f6ad55",
                    fontSize: "12px",
                    color: "#744210",
                  }}
                >
                  ⚠️ No buttons found in the template.
                </div>
              ) : (
                <select
                  value={selectedButtonText}
                  onChange={(e) => setSelectedButtonText(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "4px",
                    fontSize: "14px",
                    backgroundColor: "white",
                  }}
                >
                  <option value="">-- Select a button --</option>
                  {availableButtons.map((button) => (
                    <option key={button.id || button.text} value={button.text}>
                      🔘 {button.text}
                    </option>
                  ))}
                </select>
              )}

              {selectedButtonText && selectedButtonLoopContext && (
                <div
                  style={{
                    marginTop: "6px",
                    padding: "6px 10px",
                    backgroundColor: "#fefcbf",
                    border: "1px solid #f6ad55",
                    borderRadius: "4px",
                    fontSize: "11px",
                    color: "#744210",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span>↻</span>
                  <span>
                    This button lives inside{" "}
                    <strong>{`{% for ${selectedButtonLoopContext} in ... %}`}</strong>
                    .
                  </span>
                </div>
              )}
            </div>

            {/* Action Type — three options */}
            <div style={{ marginBottom: "12px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#4a5568",
                  marginBottom: "4px",
                }}
              >
                Action Type <span style={{ color: "#e53e3e" }}>*</span>
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => {
                    setSelectedActionType("api_config");
                    setSelectedActionId("");
                    setSelectedIntentType("");
                    setIntentFieldMapping({});
                    setIntentMappingValidity(true);
                    setResponseToRequestMapping({});
                    setSelectedApiConfigDetails(null);
                    setMappingValidity(true);
                  }}
                  style={{
                    flex: 1,
                    padding: "8px",
                    backgroundColor:
                      selectedActionType === "api_config"
                        ? "#4299e1"
                        : "#f7fafc",
                    color:
                      selectedActionType === "api_config" ? "white" : "#4a5568",
                    border: `2px solid ${selectedActionType === "api_config" ? "#4299e1" : "#e2e8f0"}`,
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight:
                      selectedActionType === "api_config" ? "600" : "400",
                  }}
                >
                  🔗 API Config
                </button>
                <button
                  onClick={() => {
                    setSelectedActionType("chain");
                    setSelectedActionId("");
                    setSelectedIntentType("");
                    setIntentFieldMapping({});
                    setIntentMappingValidity(true);
                    setResponseToRequestMapping({});
                    setSelectedApiConfigDetails(null);
                    setMappingValidity(true);
                  }}
                  style={{
                    flex: 1,
                    padding: "8px",
                    backgroundColor:
                      selectedActionType === "chain" ? "#48bb78" : "#f7fafc",
                    color: selectedActionType === "chain" ? "white" : "#4a5568",
                    border: `2px solid ${selectedActionType === "chain" ? "#48bb78" : "#e2e8f0"}`,
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: selectedActionType === "chain" ? "600" : "400",
                  }}
                >
                  ⛓️ Chain
                </button>
                <button
                  onClick={() => {
                    setSelectedActionType("skill");
                    setSelectedActionId("");
                    setSelectedIntentType("");
                    setIntentFieldMapping({});
                    setIntentMappingValidity(true);
                    setResponseToRequestMapping({});
                    setSelectedApiConfigDetails(null);
                    setMappingValidity(true);
                  }}
                  style={{
                    flex: 1,
                    padding: "8px",
                    backgroundColor:
                      selectedActionType === "skill" ? "#805ad5" : "#f7fafc",
                    color: selectedActionType === "skill" ? "white" : "#4a5568",
                    border: `2px solid ${selectedActionType === "skill" ? "#805ad5" : "#e2e8f0"}`,
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: selectedActionType === "skill" ? "600" : "400",
                  }}
                >
                  🎯 Skill
                </button>
              </div>
            </div>

            {/* SKILL SELECTOR + skill fields mapper */}
            {selectedActionType === "skill" && (
              <>
                <div style={{ marginBottom: "12px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#4a5568",
                      marginBottom: "4px",
                    }}
                  >
                    Select Skill <span style={{ color: "#e53e3e" }}>*</span>
                  </label>

                  {loadingIntents && (
                    <div
                      style={{
                        padding: "10px 12px",
                        backgroundColor: "#f7fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: "4px",
                        fontSize: "12px",
                        color: "#718096",
                      }}
                    >
                      ⏳ Loading skills...
                    </div>
                  )}

                  {!loadingIntents && intentsError && (
                    <div
                      style={{
                        padding: "10px 12px",
                        backgroundColor: "#fed7d7",
                        border: "1px solid #fc8181",
                        borderRadius: "4px",
                        fontSize: "12px",
                        color: "#9b2c2c",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span>⚠️ Failed to load skills: {intentsError}</span>
                      <button
                        onClick={async () => {
                          const realTenantId = meData?.tenant_info?.id;
                          if (!realTenantId) {
                            setIntentsError("Tenant info not yet available");
                            return;
                          }
                          setIntentsError(null);
                          setLoadingIntents(true);
                          try {
                            const list = await fetchIntents({
                              tenantId,
                              kbId,
                              region,
                            });
                            setIntents(list);
                          } catch (err) {
                            setIntentsError(
                              err.message || "Failed to load skills",
                            );
                          } finally {
                            setLoadingIntents(false);
                          }
                        }}
                        style={{
                          padding: "2px 10px",
                          backgroundColor: "white",
                          border: "1px solid #fc8181",
                          borderRadius: "4px",
                          fontSize: "11px",
                          color: "#9b2c2c",
                          cursor: "pointer",
                        }}
                      >
                        Retry
                      </button>
                    </div>
                  )}

                  {!loadingIntents && !intentsError && (
                    <select
                      value={selectedIntentType}
                      onChange={(e) => setSelectedIntentType(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "4px",
                        fontSize: "14px",
                        backgroundColor: "white",
                      }}
                    >
                      <option value="">-- Select a skill --</option>
                      {intents.map((it) => (
                        <option
                          key={it.id ?? it.intent_type}
                          value={it.intent_type}
                        >
                          {it.description
                            ? `${it.description} (${it.intent_type})`
                            : it.intent_type}
                        </option>
                      ))}
                    </select>
                  )}

                  {!loadingIntents && !intentsError && intents.length === 0 && (
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#e53e3e",
                        marginTop: "4px",
                      }}
                    >
                      No skills found for this app. Create one first.
                    </div>
                  )}
                </div>

                {/* Skill field mapping UI */}
                {selectedSkillFields.length > 0 && (
                  <div
                    style={{
                      marginBottom: "12px",
                      borderTop: "1px solid #e2e8f0",
                      paddingTop: "12px",
                    }}
                  >
                    <SkillFieldsMapper
                      skillFields={selectedSkillFields}
                      availableFields={computedAvailableResponseFields}
                      onMappingChange={handleIntentMappingChange}
                      readOnly={false}
                      initialMappings={intentFieldMapping}
                    />
                  </div>
                )}
              </>
            )}

            {/* Action Selector (api_config or chain) */}
            {selectedActionType !== "skill" && (
              <div style={{ marginBottom: "12px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#4a5568",
                    marginBottom: "4px",
                  }}
                >
                  Select{" "}
                  {selectedActionType === "api_config" ? "API Config" : "Chain"}{" "}
                  <span style={{ color: "#e53e3e" }}>*</span>
                </label>
                {loadingActions ? (
                  <div
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      color: "#718096",
                    }}
                  >
                    Loading...
                  </div>
                ) : (
                  <select
                    value={selectedActionId}
                    onChange={(e) => {
                      setSelectedActionId(e.target.value);
                      setResponseToRequestMapping({});
                      setSelectedApiConfigDetails(null);
                      setMappingValidity(true);
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "4px",
                      fontSize: "14px",
                      backgroundColor: "white",
                    }}
                  >
                    <option value="">
                      -- Select{" "}
                      {selectedActionType === "api_config"
                        ? "API Config"
                        : "Chain"}{" "}
                      --
                    </option>
                    {selectedActionType === "api_config"
                      ? availableApiConfigs.map((config) => {
                        const shortId =
                          config.api_config_id?.split("-").pop() || "";
                        return (
                          <option
                            key={config.api_config_id}
                            value={config.api_config_id}
                          >
                            {shortId}
                            {config.api_config?.url
                              ? ` — ${config.api_config.url}`
                              : ""}
                          </option>
                        );
                      })
                      : availableChains.map((chain) => (
                        <option key={chain.chain_id} value={chain.chain_id}>
                          {chain.name || chain.chain_id}
                        </option>
                      ))}
                  </select>
                )}
                {availableApiConfigs.length === 0 &&
                  selectedActionType === "api_config" &&
                  !loadingActions && (
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#e53e3e",
                        marginTop: "4px",
                      }}
                    >
                      No API configs found. Create one first.
                    </div>
                  )}
                {availableChains.length === 0 &&
                  selectedActionType === "chain" &&
                  !loadingActions && (
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#e53e3e",
                        marginTop: "4px",
                      }}
                    >
                      No chains found. Create one first.
                    </div>
                  )}
              </div>
            )}

            {/* API config mapping */}
            {selectedActionType === "api_config" &&
              selectedActionId &&
              selectedApiConfigDetails && (
                <div
                  style={{
                    marginBottom: "12px",
                    borderTop: "1px solid #e2e8f0",
                    paddingTop: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#4a5568",
                      fontWeight: "500",
                      marginBottom: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>🔄 Map Response Fields to API Parameters</span>
                    <span
                      style={{
                        fontSize: "10px",
                        color: "#718096",
                        fontWeight: "400",
                      }}
                    >
                      (Select which response field goes to each API parameter)
                    </span>
                  </div>

                  <ActionFieldsBuilder
                    apiConfig={selectedApiConfigDetails}
                    availableFields={computedAvailableResponseFields}
                    onMappingChange={handleMappingChange}
                    readOnly={false}
                    initialMappings={responseToRequestMapping}
                  />

                  {Object.keys(responseToRequestMapping).length > 0 && (
                    <div
                      style={{
                        marginTop: "8px",
                        padding: "6px 10px",
                        backgroundColor: "#f0fff4",
                        borderRadius: "4px",
                        fontSize: "11px",
                        color: "#22543d",
                        border: "1px solid #c6f6d5",
                      }}
                    >
                      ✅{" "}
                      {Object.values(responseToRequestMapping).reduce(
                        (acc, typeMappings) =>
                          acc +
                          Object.values(typeMappings).filter((v) => v).length,
                        0,
                      )}{" "}
                      fields mapped
                    </div>
                  )}
                </div>
              )}

            {actionError && (
              <div
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#fed7d7",
                  color: "#9b2c2c",
                  borderRadius: "4px",
                  fontSize: "12px",
                  marginBottom: "12px",
                }}
              >
                {actionError}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => {
                  setShowTriggerModal(false);
                  resetTriggerModal();
                }}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#f7fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "13px",
                  color: "#4a5568",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddTrigger}
                disabled={isAddTriggerDisabled}
                style={{
                  padding: "8px 16px",
                  backgroundColor: isAddTriggerDisabled ? "#e2e8f0" : "#48bb78",
                  color: isAddTriggerDisabled ? "#a0aec0" : "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: isAddTriggerDisabled ? "not-allowed" : "pointer",
                  fontSize: "13px",
                  fontWeight: "500",
                }}
              >
                {editingActionId ? "Update Trigger" : "Add Trigger"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Preview Info */}
      <div
        className={styles.controlGroup}
        style={{
          marginTop: "12px",
          padding: "10px",
          backgroundColor: "#f7fafc",
          borderRadius: "6px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            fontSize: "11px",
            fontWeight: "600",
            color: "#4a5568",
            marginBottom: "4px",
          }}
        >
          📝 Template being saved:
        </div>
        <div
          style={{
            fontSize: "11px",
            color: "#2d3748",
            wordBreak: "break-all",
            maxHeight: "60px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: "monospace",
            backgroundColor: "#edf2f7",
            padding: "4px 8px",
            borderRadius: "4px",
          }}
        >
          {safeTemplate || "(empty template)"}
        </div>
      </div>

      {/* Config Info */}
      <div
        className={styles.controlGroup}
        style={{
          marginTop: "8px",
          padding: "8px 10px",
          backgroundColor: "#f0f4ff",
          borderRadius: "6px",
          border: "1px solid #d0d8e8",
          fontSize: "11px",
          color: "#4a5568",
        }}
      >
        <div>
          <strong>config_id:</strong> {configId}
        </div>
        <div>
          <strong>kb_id:</strong> {kbId}
        </div>
        <div>
          <strong>status_code:</strong> {statusCode}
        </div>
        {modalMode && (
          <div
            style={{ color: "#9f7aea", fontWeight: "600", marginTop: "4px" }}
          >
            🔄 Modal Mode - No API calls
          </div>
        )}
      </div>

      {/* Save Button */}
      <div
        className={styles.controlGroup}
        style={{
          marginTop: "16px",
          borderTop: "1px solid #e2e8f0",
          paddingTop: "12px",
        }}
      >
        <button
          onClick={handleSave}
          disabled={isSaving || !safeTemplate.trim()}
          style={{
            width: "100%",
            padding: "8px 16px",
            backgroundColor: isSaving
              ? "#a0aec0"
              : !safeTemplate.trim()
                ? "#e2e8f0"
                : modalMode
                  ? "#9f7aea"
                  : "#48bb78",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "600",
            cursor:
              isSaving || !safeTemplate.trim() ? "not-allowed" : "pointer",
            transition: "background-color 0.2s",
            opacity: isSaving || !safeTemplate.trim() ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (!isSaving && safeTemplate.trim()) {
              e.target.style.backgroundColor = modalMode
                ? "#805ad5"
                : "#38a169";
            }
          }}
          onMouseLeave={(e) => {
            if (!isSaving && safeTemplate.trim()) {
              e.target.style.backgroundColor = modalMode
                ? "#9f7aea"
                : "#48bb78";
            }
          }}
        >
          {isSaving
            ? "⏳ Saving..."
            : !safeTemplate.trim()
              ? "📝 Enter a template first"
              : modalMode
                ? "✅ Use Template"
                : "💾 Save Configuration"}
        </button>

        {saveStatus && (
          <div
            style={{
              marginTop: "8px",
              padding: "6px 10px",
              borderRadius: "4px",
              fontSize: "11px",
              backgroundColor:
                saveStatus.type === "success" ? "#c6f6d5" : "#fed7d7",
              color: saveStatus.type === "success" ? "#22543d" : "#9b2c2c",
              border: `1px solid ${saveStatus.type === "success" ? "#9ae6b4" : "#feb2b2"}`,
            }}
          >
            {saveStatus.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default Controls;
